import * as React from 'react';
import * as Sentry from '@sentry/react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider, createRoutesFromElements, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { combineReducers, createStore, applyMiddleware, compose } from 'redux';
import { createLogger } from 'redux-logger';
import { thunk } from 'redux-thunk';
import { superpathjoin } from 'superpathjoin';
import { selectIsAuthenticatedUser, selectAuthenticatedUserInfos } from '@gandi/authentication';
import ConfigBag from '@gandi/config-bag';
import { configReducer } from '@gandi/redux-config';
import { selectCurrentId, selectPersonalId } from '@gandi/redux-sharing';
import { invalidateFailures } from '@gandi/resource/redux';
import { setConfiguration, setPrivacyMode, getPrivacyMode } from '@gandi/tracking';
import { createTranslator, localeSelector, reducer as localeReducer } from '@gandi/translate';
import { Layers } from '@gandi/ui';
import { createAppLogger } from 'gandi.redux-logger';
import { initFetcherMiddleware, pageMetaMiddleware } from '../redux/middlewares';
import { baseNameRouter, sanitize } from '../utils';
import * as sentryConfig from './sentryConfig';
import { jsx as _jsx } from "react/jsx-runtime";
function createFetcherMiddleware(__, config, locale) {
  const clientUri = superpathjoin(config.get('app.endpoint'), config.get('fetcher.endpoint', '/api/v5'), true);
  return initFetcherMiddleware(console, __, 'client', clientUri, locale)({});
}
function getCookie(name) {
  const cookie = {};
  document.cookie.split(';').forEach(el => {
    const [k, v] = el.split('=');
    cookie[k.trim()] = decodeURIComponent(v.trim());
  });
  return cookie[name];
}
const ensureDOMReady = () => new Promise(resolve => {
  if (document.readyState !== 'loading') {
    resolve();
    return;
  }
  document.addEventListener('DOMContentLoaded', resolve);
});
const _routerConfig = baseNameRouter.client;
const client = async ({
  routes,
  reducers,
  middlewares = [],
  routerConfig = _routerConfig,
  anonymousSupport = false,
  globals = {}
}) => {
  await ensureDOMReady();
  const initialState = window.INITIAL_STATE;
  const i18n = {
    ...window.I18N,
    sanitize
  };
  const config = new ConfigBag();
  config.merge(initialState.config);
  if (window.navigator.doNotTrack !== '1') {
    setConfiguration({
      site: config.get('tracking.id', undefined),
      collectDomain: config.get('tracking.collectDomain', undefined),
      debug: config.get('tracking.debug', false)
    });
    const privacyMode = getCookie('atauthority');
    if (privacyMode?.val?.visitor_mode && privacyMode?.val?.visitor_mode !== getPrivacyMode()) {
      setPrivacyMode(privacyMode?.val?.visitor_mode);
    }
  }
  if (config.get('sentry.enabled', false)) {
    window.onerror = undefined;
    Sentry.init({
      ...sentryConfig,
      dsn: config.get('sentry.dsn'),
      environment: config.get('sentry.environment'),
      release: config.get('sentry.release'),
      initialScope: {
        tags: {
          'app.name': config.get('sentry.tags.appName')
        }
      },
      debug: config.get('sentry.debug', false),
      normalizeDepth: config.get('sentry.normalizeDepth', 6),
      integrations: [Sentry.captureConsoleIntegration({
        levels: ['error']
      }), Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        instrumentPageLoad: true,
        instrumentNavigation: true,
        beforeStartSpan: context => {
          return {
            ...context,
            name: location.pathname.replace(/^[0-9a-f]{8}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{12}$/g, '/<hash>')
          };
        }
      })],
      beforeSend: event => {
        if (!event.request?.data) {
          return event;
        }
        const size = JSON.stringify(event.request.data || '').length;
        if (size > 500000) {
          event.request = event.request?.data?.slice(0, 500000);
        }
        return event;
      },
      tracesSampleRate: 1.0,
      tracePropagationTargets: ['admin.stage.gandi.net', 'admin.gandi.net', 'shop.stage.gandi.net', 'shop.gandi.net', /^\/api\//],
      sendClientReports: false
    });
    (window._r || []).forEach(err => {
      Sentry.captureException(err);
    });
    window._r = undefined;
    const locale = localeSelector(initialState);
    if (selectIsAuthenticatedUser(initialState)) {
      const userInfos = selectAuthenticatedUserInfos(initialState);
      const currentId = selectCurrentId(initialState);
      const personalId = selectPersonalId(initialState);
      const {
        username,
        email
      } = userInfos;
      Sentry.setContext('user', {
        id: personalId,
        username,
        locale,
        email,
        'Current Organization ID': currentId,
        'Personal Organization ID': personalId
      });
    } else {
      Sentry.setContext('user', {
        username: 'anonymous',
        locale
      });
    }
  }
  const node = document.querySelector(config.get('appCssSelector'));
  const appEndpoint = config.get('app.endpoint');
  __webpack_public_path__ = superpathjoin(appEndpoint, __webpack_public_path__, true);
  const rootReducer = combineReducers({
    locale: localeReducer,
    config: configReducer,
    ...reducers
  });
  const translator = createTranslator(i18n);
  const mdlewrs = [thunk];
  if (globals.__ENV__ === 'development') {
    mdlewrs.push(createAppLogger());
    mdlewrs.push(createLogger({
      logger: console,
      collapsed: true
    }));
  }
  const orderedMiddlewares = Array.isArray(middlewares) ? {
    beforeFetcher: [],
    afterFetcher: middlewares
  } : {
    beforeFetcher: middlewares.beforeFetcher || [],
    afterFetcher: middlewares.afterFetcher || []
  };
  mdlewrs.push(pageMetaMiddleware, ...orderedMiddlewares.beforeFetcher, createFetcherMiddleware(translator.translate, config, i18n.locale), ...orderedMiddlewares.afterFetcher);
  if (globals.__DEV__) {
    const freezeState = require('../redux/middlewares/freezeState').freezeState;
    mdlewrs.push(freezeState);
  }
  const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer: action => {
      if (action?.meta?.field === 'password') {
        return null;
      }
      return action;
    },
    stateTransformer: state => {
      return {
        ...state,
        config: '-removed-',
        gandiReduxResource: '-removed-'
      };
    }
  });
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  const store = createStore(rootReducer, initialState, composeEnhancers(applyMiddleware(...mdlewrs), sentryReduxEnhancer));
  const assetPrefix = globals.__DEV__ ? 'assets' : 'dist/public';
  const extraRouteProps = {
    assetEndpoint: appEndpoint,
    assetPrefix,
    manifestContent: window.MANIFEST,
    translator,
    store
  };
  const fetcherEndpoint = config.get('fetcher.endpoint', '/api/v5');
  const pivoineEndpoint = superpathjoin(appEndpoint, fetcherEndpoint, true);
  routerConfig(appEndpoint, anonymousSupport, pivoineEndpoint)(store).then(routerOptions => {
    const elements = routes(extraRouteProps);
    const finalRoutes = createRoutesFromElements(elements);
    store.dispatch(invalidateFailures({}));
    const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);
    const router = sentryCreateBrowserRouter(finalRoutes, routerOptions);
    const app = /*#__PURE__*/_jsx(React.StrictMode, {
      children: /*#__PURE__*/_jsx(Layers, {
        children: /*#__PURE__*/_jsx(React.Suspense, {
          fallback: null,
          children: /*#__PURE__*/_jsx(Provider, {
            store: store,
            children: /*#__PURE__*/_jsx(RouterProvider, {
              router: router,
              ...routerOptions
            })
          })
        })
      })
    });
    hydrateRoot(node, app);
  });
  if (!globals.__DEV__) {
    try {
      console.info('ₒ  Gandi.net\n' + 'ᘜ  #no_bullshit\n\n' + "Hello developer, you're reading, we are hiring\n" + 'See https://gandi.jobs ' + 'or mailto:jobs@gandi.net :)');
    } catch (err) {}
  }
};
export { client };